import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";

export const HOVER_INTENT_DELAY_MS = 100;

export interface HoverIntent<T> {
    start: (value: T) => void;
    end: (value: T) => void;
}

export interface UseHoverIntentOptions {
    delay?: number;
}

interface PendingIntent<T> {
    value: T;
    timer: ReturnType<typeof setTimeout>;
}

export function useHoverIntent<T>(onIntent: ((value: T) => void) | undefined, options: UseHoverIntentOptions = {}): HoverIntent<T> {
    const { delay = HOVER_INTENT_DELAY_MS } = options;

    const onIntentRef = useRef(onIntent);
    const pendingRef = useRef<PendingIntent<T> | undefined>(undefined);

    useLayoutEffect(() => {
        onIntentRef.current = onIntent;
    });

    useEffect(
        () => () => {
            clearTimeout(pendingRef.current?.timer);
            pendingRef.current = undefined;
        },
        []
    );

    const start = useCallback(
        (value: T) => {
            clearTimeout(pendingRef.current?.timer);
            pendingRef.current = undefined;

            if (!onIntentRef.current) {
                return;
            }

            const timer = setTimeout(() => {
                pendingRef.current = undefined;
                onIntentRef.current?.(value);
            }, delay);

            pendingRef.current = { value, timer };
        },
        [delay]
    );

    const end = useCallback((value: T) => {
        if (pendingRef.current?.value !== value) {
            return;
        }

        clearTimeout(pendingRef.current.timer);
        pendingRef.current = undefined;
    }, []);

    return useMemo(() => ({ start, end }), [start, end]);
}
